import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControlLabel,
    Switch,
    Box,
    Alert,
    CircularProgress,
    Typography,
    IconButton,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Customer } from "../../types";
import { customerService } from "../../services/customerService";
import { CustomerAddressEditor, EditableAddress } from "../customers/CustomerAddressEditor";
import { validateAndNormalizeBdPhone } from "../../utils/phone";
const addressSchema = yup.object({
    address_type: yup.string().required("Address type is required"),
    street_address: yup.string().required("Street address is required"),
    city: yup.string().required("City is required"),
    map_link: yup
        .string()
        .transform((value) => value || undefined)
        .optional(),
    is_default: yup.boolean().optional(),
});

const schema = yup.object({
    first_name: yup.string().required("First name is required"),
    last_name: yup
        .string()
        .transform((value) => value || undefined)
        .optional(),
    email: yup
        .string()
        .email("Invalid email")
        .transform((value) => value || undefined)
        .optional(),
    phone: yup
        .string()
        .transform((value) => value || undefined)
        .optional()
        .test(
            "bd-phone",
            "Phone number must start with 01 or +8801",
            (value) => !value || validateAndNormalizeBdPhone(value).valid,
        ),
    alternative_phone: yup
        .string()
        .transform((value) => value || undefined)
        .optional()
        .test(
            "bd-phone",
            "Phone number must start with 01 or +8801",
            (value) => !value || validateAndNormalizeBdPhone(value).valid,
        ),
    date_of_birth: yup
        .string()
        .transform((value) => value || undefined)
        .optional(),
    profile: yup
        .string()
        .transform((value) => value || undefined)
        .optional(),
    is_active: yup.boolean().optional(),
    marketing_consent: yup.boolean().optional(),
    newsletter_subscription: yup.boolean().optional(),
    note: yup
        .string()
        .transform((value) => value || undefined)
        .optional(),
    addresses: yup.array().of(addressSchema).optional(),
});

interface CustomerFormData {
    first_name: string;
    last_name?: string;
    email?: string;
    phone?: string;
    alternative_phone?: string;
    date_of_birth?: string;
    profile?: string;
    is_active?: boolean;
    marketing_consent?: boolean;
    newsletter_subscription?: boolean;
    note?: string;
    addresses?: EditableAddress[];
}

interface CustomerFormProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (customer: Customer) => void;
    customer?: Customer | null;
    mode: "create" | "edit";
}

export const CustomerForm: React.FC<CustomerFormProps> = ({
    open,
    onClose,
    onSubmit,
    customer,
    mode,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [addresses, setAddresses] = useState<EditableAddress[]>([]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<CustomerFormData>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            first_name: "",
            last_name: "",
            email: "",
            phone: "",
            alternative_phone: "",
            date_of_birth: "",
            profile: "",
            is_active: true,
            marketing_consent: false,
            newsletter_subscription: false,
            note: "",
            addresses: [],
        },
    });

    useEffect(() => {
        if (open) {
            if (customer && mode === "edit") {
                reset({
                    first_name: customer.first_name,
                    last_name: customer.last_name,
                    email: customer.email || "",
                    phone: customer.phone || "",
                    alternative_phone: customer.alternative_phone || "",
                    date_of_birth: customer.date_of_birth || "",
                    profile: customer.profile || "",
                    is_active: customer.status === "ACTIVE",
                    marketing_consent: customer.marketing_consent,
                    newsletter_subscription: customer.newsletter_subscription,
                    note: customer.note || "",
                });
                // Load existing addresses
                if (customer.addresses) {
                    setAddresses(
                        customer.addresses.map((addr) => ({
                            address_type: addr.address_type,
                            street_address: addr.street_address,
                            city: addr.city,
                            map_link: addr.map_link || "",
                            is_default: addr.is_default,
                        })),
                    );
                }
            } else {
                reset({
                    first_name: "",
                    last_name: "",
                    email: "",
                    phone: "",
                    alternative_phone: "",
                    date_of_birth: "",
                    profile: "",
                    is_active: true,
                    marketing_consent: false,
                    newsletter_subscription: false,
                    note: "",
                    addresses: [],
                });
                setAddresses([]);
            }
        }
    }, [open, customer, mode, reset]);

    const handleFormSubmit = async (data: CustomerFormData) => {
        try {
            setLoading(true);
            setError("");

            // Clean up addresses - convert empty strings to undefined
            const cleanedAddresses = addresses.map((addr) => ({
                ...addr,
                map_link: addr.map_link || undefined,
            }));

            const customerData = {
                ...data,
                last_name: data.last_name || undefined,
                email: data.email || undefined,
                phone: data.phone
                    ? validateAndNormalizeBdPhone(data.phone).normalized
                    : undefined,
                alternative_phone: data.alternative_phone
                    ? validateAndNormalizeBdPhone(data.alternative_phone).normalized
                    : undefined,
                date_of_birth: data.date_of_birth || undefined,
                profile: data.profile || undefined,
                is_active: data.is_active ?? true,
                marketing_consent: data.marketing_consent ?? false,
                newsletter_subscription: data.newsletter_subscription ?? false,
                note: data.note || undefined,
                addresses:
                    cleanedAddresses.length > 0 ? cleanedAddresses : undefined,
            };

            console.log("Submitting customer data:", customerData);
            console.log("Addresses being sent:", cleanedAddresses);

            let result: Customer;
            if (mode === "create") {
                result = await customerService.createCustomer(customerData);
            } else {
                result = await customerService.updateCustomer(
                    customer!.customer_id,
                    customerData,
                );
            }

            onSubmit(result);
            onClose();
        } catch (err: any) {
            console.error("Customer form error:", err);

            let errorMessage = `Failed to ${mode} customer`;

            if (err.response?.data) {
                const errorData = err.response.data;

                // Handle FastAPI validation errors
                if (Array.isArray(errorData)) {
                    errorMessage = errorData
                        .map(
                            (error: any) =>
                                `${error.loc?.join(" -> ") || "Field"}: ${error.msg}`,
                        )
                        .join(", ");
                } else if (errorData.detail) {
                    errorMessage =
                        typeof errorData.detail === "string"
                            ? errorData.detail
                            : JSON.stringify(errorData.detail);
                } else {
                    errorMessage = JSON.stringify(errorData);
                }
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        reset();
        setError("");
        setAddresses([]);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pr: 1,
                }}
            >
                {mode === "create" ? "Add New Customer" : "Edit Customer"}
                {isMobile && (
                    <IconButton
                        edge="end"
                        onClick={handleClose}
                        aria-label="close"
                        size="small"
                    >
                        <Close />
                    </IconButton>
                )}
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box component="form" sx={{ mt: 1 }}>
                    {/* Basic Customer Information */}
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Customer Information
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            mb: 3,
                        }}
                    >
                        <Box sx={{ flex: "1 1 300px" }}>
                            <Controller
                                name="first_name"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        margin="normal"
                                        required
                                        fullWidth
                                        label="First Name"
                                        error={!!errors.first_name}
                                        helperText={errors.first_name?.message}
                                    />
                                )}
                            />
                        </Box>

                        <Box sx={{ flex: "1 1 300px" }}>
                            <Controller
                                name="last_name"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        margin="normal"
                                        fullWidth
                                        label="Last Name (Optional)"
                                        error={!!errors.last_name}
                                        helperText={errors.last_name?.message}
                                    />
                                )}
                            />
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            mb: 3,
                        }}
                    >
                        <Box sx={{ flex: "1 1 300px" }}>
                            <Controller
                                name="email"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        margin="normal"
                                        fullWidth
                                        label="Email (Optional)"
                                        type="email"
                                        error={!!errors.email}
                                        helperText={errors.email?.message}
                                    />
                                )}
                            />
                        </Box>

                        <Box sx={{ flex: "1 1 300px" }}>
                            <Controller
                                name="phone"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        margin="normal"
                                        fullWidth
                                        label="Phone"
                                        error={!!errors.phone}
                                        helperText={errors.phone?.message}
                                    />
                                )}
                            />
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            mb: 3,
                        }}
                    >
                        <Box sx={{ flex: "1 1 300px" }}>
                            <Controller
                                name="alternative_phone"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        margin="normal"
                                        fullWidth
                                        label="Alternative Phone (Optional)"
                                        error={!!errors.alternative_phone}
                                        helperText={
                                            errors.alternative_phone?.message
                                        }
                                    />
                                )}
                            />
                        </Box>

                        <Box sx={{ flex: "1 1 300px" }}>
                            <Controller
                                name="date_of_birth"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        margin="normal"
                                        fullWidth
                                        label="Date of Birth"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        error={!!errors.date_of_birth}
                                        helperText={
                                            errors.date_of_birth?.message
                                        }
                                    />
                                )}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Controller
                            name="profile"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    margin="normal"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    label="Profile (Optional)"
                                    placeholder="URLs, social profiles, or general information about the customer"
                                    error={!!errors.profile}
                                    helperText={
                                        errors.profile?.message ||
                                        "Can store URLs, multiple links, or any relevant customer information"
                                    }
                                />
                            )}
                        />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Controller
                            name="note"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    margin="normal"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Notes (Optional)"
                                    error={!!errors.note}
                                    helperText={errors.note?.message}
                                />
                            )}
                        />
                    </Box>

                    {/* Customer Preferences */}
                    <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                        Preferences
                    </Typography>

                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Controller
                            name="marketing_consent"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            checked={field.value}
                                        />
                                    }
                                    label="Marketing Consent"
                                />
                            )}
                        />

                        <Controller
                            name="newsletter_subscription"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            checked={field.value}
                                        />
                                    }
                                    label="Newsletter Subscription"
                                />
                            )}
                        />

                        <Controller
                            name="is_active"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            checked={field.value}
                                        />
                                    }
                                    label="Active"
                                />
                            )}
                        />
                    </Box>

                    {/* Addresses Section */}
                    <CustomerAddressEditor addresses={addresses} onChange={setAddresses} />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit(handleFormSubmit)}
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? (
                        <CircularProgress size={24} />
                    ) : mode === "create" ? (
                        "Create"
                    ) : (
                        "Update"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
